/**
 * The cellars the owner's selector offers (T070, FR-040, contracts/storage-and-permissions.md
 * "Owner's cellar selector"): the owner's own cellar first, then every other cellar that exists,
 * named from its profile.json and ordered by that name.
 */
import { cellarName, profileDisplayName, type Profile } from '../domain/profile';
import { t, tf } from '../i18n';
import type { ObjectStorage } from '../store/fake-storage';
import { readProfile } from '../store/profile-store';

export interface CellarEntry {
  /** The cellar id, which is also the person's id. */
  id: string;
  /** The profile's name or email; the id itself if the profile is missing, never hidden. */
  name: string;
  own: boolean;
  /** The name its person gave the cellar under Réglages (owner, 2026-10-08), if any. */
  cellarName?: string;
}

/** A cellar's entry from its profile (undefined when unreadable). */
export function cellarEntry(id: string, own: boolean, profile: Profile | undefined): CellarEntry {
  const name = profile ? profileDisplayName(profile) : own ? '' : id;
  const named = profile ? cellarName(profile) : undefined;
  return { id, name, own, ...(named !== undefined ? { cellarName: named } : {}) };
}

const CELLAR_DOCUMENT = /^cellars\/([^/]+)\/(?:cellar|profile)\.json$/;

const byName = (a: CellarEntry, b: CellarEntry) =>
  cellarLabel(a).localeCompare(cellarLabel(b), 'fr', { sensitivity: 'base' }) ||
  a.id.localeCompare(b.id);

export async function listCellars(storage: ObjectStorage, ownId: string): Promise<CellarEntry[]> {
  const ids = new Set<string>();
  for (const path of await storage.list('cellars/')) {
    const id = CELLAR_DOCUMENT.exec(path)?.[1];
    if (id !== undefined) ids.add(id);
  }
  ids.add(ownId);

  const entries = await Promise.all(
    [...ids].map(async (id): Promise<CellarEntry> => {
      const own = id === ownId;
      // A profile too new for this code, or damaged, never hides the cellar: it shows its id.
      const profile = await readProfile(storage, id).catch(() => undefined);
      return cellarEntry(id, own, profile);
    }),
  );

  const own = entries.filter((entry) => entry.own);
  const others = entries.filter((entry) => !entry.own).sort(byName);
  return [...own, ...others];
}

/**
 * How a cellar is named on every screen (FR-040, SC-014): the name its person gave it under
 * Réglages (owner, 2026-10-08), else "Ma cave" for the person's own and "Cave de {name}" for
 * anyone else's.
 */
export function cellarLabel(entry: CellarEntry): string {
  if (entry.cellarName) return entry.cellarName;
  return entry.own ? t('cellar.mine') : tf('cellar.of', { name: entry.name });
}
